/**
 * Story 183 (jupiter #3017) — the night character of a JOB node (Morte night planning).
 *
 * The night job is a property OF THE JOB, declared by the planner with a checkbox: `jobReference.nightJob`
 * (C1 of the frozen contract). Sun resolves the night technology when it plans; the client never knows
 * the suffix nor the night technology's code (RN4, §8.4), so whether a technology admits the mark is a
 * fact Sun computes and sends as `technology.nightEquivalentAvailable` (C2).
 *
 * RN7: this module is the SINGLE owner of the predicate and of the gating in Jupiter. The graph's node
 * panel and the Process Tree job form both read them from here, so the two surfaces can never disagree
 * for the same node. Copying a formula into a component is what a test forbids.
 */
import { isExternalEffective } from './jobExternalCharacter'

/**
 * What a surface needs to know about the night job beyond the jobReference it edits: whether the
 * feature is active in this installation (C4, `frontEndConfig.nightJobEnabled`) and which job nodes of
 * the workflow currently carry the mark. With the feature off the control does not exist (RN6.3).
 */
export const NIGHT_JOB_CONTEXT_OFF = Object.freeze({ enabled: false, nightJobNodeIds: [] })

/**
 * C4 — whether the night job feature is active in this installation, as `useFrontEndConfig()` serves it.
 * Sun computes it (`morte.planner.enabled` and suffix and native planner); the client only reads it.
 * Strict: a configuration not loaded yet, or a Sun that does not send the field, reads as OFF (RN6.3).
 *
 * @param {{nightJobEnabled?: boolean}} [frontEndConfig]
 * @returns {boolean}
 */
export const isNightJobFeatureEnabled = (frontEndConfig) => {
  return frontEndConfig?.nightJobEnabled === true
}

/**
 * The predicate itself: the planner's mark, and nothing else. Strict comparison on purpose — the same
 * fail-safe as `isExternalEffective`: anything but `true` reads as a day job.
 *
 * @param {{nightJob?: boolean}} [jobReference]
 * @returns {boolean}
 */
export const isNightJobEffective = (jobReference) => {
  return jobReference?.nightJob === true
}

/**
 * Why the "Night job" control is not actionable: one reason per factor of RN6, so the tooltip names the
 * actual cause. The values are i18n keys and must exist in the 8 locale files (S30).
 */
export const NIGHT_BLOCK_REASON = {
  NOT_EDITING: 'The workflow is not being edited',
  FABRICATION_ORDER_CLOSED: 'The fabrication order is closed',
  JOB_ALREADY_STARTED: 'The job has already started',
  NO_NIGHT_EQUIVALENT: 'The technology of this job has no night equivalent',
  ANOTHER_NIGHT_JOB: 'Another job of this workflow is already the night job',
  EXTERNAL_JOB: 'An external job cannot be the night job'
}

/**
 * RN6.6 (D13) — the reason the EXTERN control gives while its job is the night job. An i18n key (S30).
 */
export const EXTERN_NIGHT_BLOCK_REASON = 'The night job cannot be external'

/**
 * C2 — whether the technology of the job admits the night mark. Sun computes it (RN4): the client never
 * knows the suffix, so it never re-derives the convention. Strict on purpose: an absent or malformed
 * value reads as "no equivalent", the fail-safe direction (the mark cannot be put on a job Sun would
 * reject with E-S2).
 *
 * @param {{nightEquivalentAvailable?: boolean}} [technology]
 * @returns {boolean}
 */
const hasNightEquivalent = (technology) => technology?.nightEquivalentAvailable === true

/**
 * Whether the "Night job" control is actionable and, when it is not, which single reason to explain.
 *
 * @param {Object} params
 * @param {{technology?: Object, nightJob?: boolean, hasParts?: boolean}} [params.jobReference] `hasParts`
 *   (C3) absent — a template, where Sun does not compute it — reads as not started and does NOT block.
 * @param {boolean} [params.editing] Whether the workflow is in edition; anything but `true` blocks.
 * @param {boolean} [params.fabricationOrderOperable] Absent (template or subworkflow, which have no FO)
 *   reads as operable and does NOT block — the editor's own prop-chain convention, as for Extern.
 * @param {boolean} [params.anotherNightJob] Whether ANOTHER job of the workflow already carries the
 *   mark (D1: there is no "marking one unmarks the previous"; the planner unmarks first).
 * @returns {{actionable: boolean, blockReason: string}}
 */
export const resolveNightCheckboxGating = ({ jobReference, editing, fabricationOrderOperable = true, anotherNightJob = false } = {}) => {
  if (editing !== true) {
    return { actionable: false, blockReason: NIGHT_BLOCK_REASON.NOT_EDITING }
  }
  if (fabricationOrderOperable !== true) {
    return { actionable: false, blockReason: NIGHT_BLOCK_REASON.FABRICATION_ORDER_CLOSED }
  }
  // RN6 factor 3 and RN6.1: a started job keeps whatever mark it has, in either direction (V3, E-S3).
  if (jobReference?.hasParts === true) {
    return { actionable: false, blockReason: NIGHT_BLOCK_REASON.JOB_ALREADY_STARTED }
  }
  if (!hasNightEquivalent(jobReference?.technology)) {
    return { actionable: false, blockReason: NIGHT_BLOCK_REASON.NO_NIGHT_EQUIVALENT }
  }
  if (anotherNightJob === true) {
    return { actionable: false, blockReason: NIGHT_BLOCK_REASON.ANOTHER_NIGHT_JOB }
  }
  // RN6 factor 6 (D13, RN18): a subcontracted job is not worked by our night machines. The external
  // character is read from ITS single owner, never re-derived here.
  if (isExternalEffective(jobReference)) {
    return { actionable: false, blockReason: NIGHT_BLOCK_REASON.EXTERNAL_JOB }
  }
  return { actionable: true, blockReason: '' }
}

/**
 * RN2 — the census of the night job: the ids of the JOB nodes of the workflow that carry the mark, in
 * model order (depth first). It counts the jobs of the nested subworkflows at ANY depth (D14: the planner
 * flattens the flow), as far as they are loaded — a set still a skeleton contributes nothing. It is
 * DERIVED from the live model of the editor on every render (it never travels in the save), so ticking a
 * box moves it at once, before any save.
 *
 * @param {{nodes?: Array<{id: string, nodeType: string, jobReference?: Object, subWorkflow?: Object}>}} [workflow]
 * @returns {string[]}
 */
export const collectNightJobNodeIds = (workflow) => {
  const ids = []
  const walk = (nodes) => {
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      if (node?.nodeType === 'WORKFLOW') {
        walk(node.subWorkflow?.nodes)
        return
      }
      if (node?.nodeType === 'JOB' && isNightJobEffective(node.jobReference)) {
        ids.push(node.id)
      }
    })
  }
  walk(workflow?.nodes)
  return ids
}

/**
 * RN6 factor 5 — whether a job OTHER than this node is the night job. The night job itself is never
 * "another": its own control stays actionable so that it can be unmarked.
 *
 * @param {{nightJobNodeIds?: string[]}} [nightJobContext]
 * @param {string} nodeId
 * @returns {boolean}
 */
export const isAnotherJobTheNightJob = (nightJobContext, nodeId) => {
  return (nightJobContext?.nightJobNodeIds || []).some((id) => id !== nodeId)
}

/**
 * RN6.6 (D13) — the symmetry on the Extern control: while its job is the night job, Extern is blocked
 * with its own reason; to make the job external the planner unmarks it first. This only LAYERS the night
 * block over the Extern gating, which keeps its single owner (`resolveExternCheckboxGating`):
 *
 * - with the feature inactive, or a job that is not the night job, the Extern gating is returned AS IS —
 *   the very same object — so nothing about Extern changes (RN21, S88);
 * - a reason Extern already had (not editing, started, FO closed) is kept: it is the more enclosing
 *   one, the principle RN6.2 states for ordering reasons.
 *
 * @param {{actionable: boolean, blockReason: string}} externGating What `resolveExternCheckboxGating` said
 * @param {{nightJob?: boolean}} [jobReference]
 * @param {{enabled?: boolean}} [nightJobContext]
 * @returns {{actionable: boolean, blockReason: string}}
 */
export const resolveExternGatingWithNightMark = (externGating, jobReference, nightJobContext) => {
  if (nightJobContext?.enabled !== true || !isNightJobEffective(jobReference) || !externGating.actionable) {
    return externGating
  }
  return { actionable: false, blockReason: EXTERN_NIGHT_BLOCK_REASON }
}

/**
 * RN6.4 (D12) — the warning shown when a technology change removes the night mark. An i18n key (S30).
 */
export const NIGHT_MARK_REMOVED_WARNING = 'The night mark was removed because the new technology has no night equivalent'

/**
 * RN6.4 (D12) — after the technology of a job changes on screen (before saving), a night job whose NEW
 * technology admits no night mark loses it in the same act, and the caller must warn the planner: a job
 * never stays marked on a technology that does not admit it without the user having seen it. The
 * technology is the one picked from the palette, which brings its own C2.
 *
 * GATE 2: it only acts with the feature ACTIVE. With it inactive the mark travels untouched (RN21).
 *
 * @param {{nightJob?: boolean, technology?: Object}} jobReference The job reference WITH the new technology
 * @param {boolean} featureEnabled C4
 * @returns {{jobReference: Object, markRemoved: boolean}}
 */
export const dropNightMarkWithoutEquivalent = (jobReference, featureEnabled) => {
  if (featureEnabled !== true || !isNightJobEffective(jobReference) || hasNightEquivalent(jobReference?.technology)) {
    return { jobReference, markRemoved: false }
  }
  return { jobReference: { ...jobReference, nightJob: false }, markRemoved: true }
}

/**
 * C1, RN8.1 (D8) — the save payload with `nightJob` EXPLICIT on every job, nested subworkflows included.
 *
 * Sun resolves an ABSENT mark as "keep the persisted one", so a user-session save that omitted it could
 * never change it. Every save route of a user session (the template editor, the FO window and the FO
 * detail dialog) therefore sends the field always: `true` where the job carries the mark, `false`
 * everywhere else. It is sent whatever C4 says: with the feature inactive the mark still travels as it
 * came (RN21, S88).
 *
 * Pure: returns new objects for what it normalises and never touches the editor's model. A subworkflow
 * still a skeleton is sent as it is — it has no jobs in the payload to normalise.
 *
 * @param {{nodes?: Array<Object>}} workflow The workflow about to be sent
 * @returns {Object} The same workflow with `nightJob` explicit on every job
 */
export const withExplicitNightJob = (workflow) => {
  const normaliseNodes = (nodes) => nodes.map((node) => {
    if (node?.nodeType === 'WORKFLOW' && Array.isArray(node.subWorkflow?.nodes)) {
      return { ...node, subWorkflow: { ...node.subWorkflow, nodes: normaliseNodes(node.subWorkflow.nodes) } }
    }
    if (node?.nodeType === 'JOB' && node.jobReference) {
      return { ...node, jobReference: { ...node.jobReference, nightJob: isNightJobEffective(node.jobReference) } }
    }
    return node
  })
  if (!Array.isArray(workflow?.nodes)) {
    return workflow
  }
  return { ...workflow, nodes: normaliseNodes(workflow.nodes) }
}
